<template>
  <div class="bg-white padding-five-all">
    <h5 class="color-orange">{{ messages.FILTERS_TITLE }}</h5>

    <b-container fluid>
      <b-row class="padding-ten-tb align-items-end">
        <b-col
          v-for="slot in filterSlots"
          :key="slot"
          cols="12"
          :lg="filterColLg"
          class="mb-3 mb-lg-0"
        >
          <template v-if="slot === 'periodo'">
            <label class="text-grey">{{ periodoLabel }}</label>
            <b-form-select
              class="grey-input"
              :value="filters.idPeriodo"
              :options="periodoOptions"
              :disabled="loading || loadingCatalogs"
              @input="updateFilter('idPeriodo', $event)"
            />
          </template>

          <template v-else-if="slot === 'estado'">
            <label class="text-grey">{{ messages.FILTER_ESTADO_LABEL }}</label>
            <b-form-select
              class="grey-input"
              :value="filters.estado"
              :options="estadoOptions"
              :disabled="loading || loadingCatalogs"
              @input="updateFilter('estado', $event)"
            />
          </template>

          <template v-else-if="slot === 'proveedor'">
            <label class="text-grey">{{ messages.FILTER_PROVEEDOR_LABEL }}</label>
            <b-form-select
              class="grey-input"
              :value="filters.idProv"
              :options="proveedorOptions"
              :disabled="loading || loadingCatalogs"
              @input="updateFilter('idProv', $event)"
            />
          </template>

          <template v-else-if="slot === 'tienda'">
            <label class="text-grey">{{ messages.FILTER_TIENDA_LABEL }}</label>
            <ComboStore
              :value="filters.idTienda"
              :disabled="loading || loadingCatalogs"
              @input="updateFilter('idTienda', $event)"
            />
          </template>
        </b-col>
      </b-row>

      <b-row class="align-items-center">
        <b-col
          cols="12"
          lg="6"
          class="mb-3 mb-lg-0 text-center text-lg-left"
        >
          <b-button
            class="bg-grey-btn hidden-icon"
            :disabled="loading"
            @click="$emit('clear')"
          >
            <font-awesome-icon icon="broom" />
            {{ messages.CLEAR_BUTTON }}
          </b-button>
        </b-col>

        <b-col
          cols="12"
          lg="6"
          class="mb-3 mb-lg-0 text-center text-lg-right"
        >
          <b-button
            class="btn-orange hidden-icon"
            :disabled="loading || exporting"
            @click="$emit('export')"
          >
            <font-awesome-icon icon="file-excel" />
            {{ messages.EXPORT_BUTTON }}
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import ComboStore from "@/components/ComboStore.vue";
import { isProveedor } from "@/constants/perfiles";
import {
  getLiquidacionEstados,
  getLiquidacionPeriodos,
  getProviders,
} from "@/assets/js/common";
import {
  MESSAGES,
  filterLiquidacionEstadoSelectOptions,
  getDefaultEstadoFilterOptions,
  getDefaultPeriodoFilterOptions,
  getDefaultProveedorFilterOptions,
  getReportFilterSlots,
} from "@/services/liquidacion-reportes/liquidacion-reportes.constants";
import { normalizeFiltroLiquidacionFilters } from "@/services/liquidacion-reportes/liquidacion-reportes.contract";

export default {
  name: "LiquidacionReportesFiltersPanel",

  components: {
    ComboStore,
  },

  props: {
    filters: {
      type: Object,
      required: true,
    },
    reportType: {
      type: String,
      required: true,
      validator: (value) => ["resumen", "detallado"].includes(value),
    },
    perfil: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    exporting: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      messages: MESSAGES,
      periodoOptions: getDefaultPeriodoFilterOptions(),
      estadoOptions: getDefaultEstadoFilterOptions(),
      proveedorOptions: getDefaultProveedorFilterOptions(),
      loadingCatalogs: false,
    };
  },

  computed: {
    isProveedorProfile() {
      return isProveedor(this.perfil);
    },

    filterSlots() {
      return getReportFilterSlots(this.reportType, this.isProveedorProfile);
    },

    periodoLabel() {
      return this.isProveedorProfile
        ? this.messages.FILTER_PERIODO_LABEL
        : this.messages.FILTER_CORTE_LABEL;
    },

    filterColLg() {
      const count = this.filterSlots.length;
      if (!count) return 12;
      return Math.floor(12 / count);
    },
  },

  created() {
    this.loadCatalogOptions();
  },

  methods: {
    async loadCatalogOptions() {
      this.loadingCatalogs = true;

      const tasks = [];

      if (this.filterSlots.includes("periodo")) {
        tasks.push({
          run: () => getLiquidacionPeriodos(),
          onSuccess: (options) => {
            this.periodoOptions = options;
          },
          onError: () => {
            this.periodoOptions = getDefaultPeriodoFilterOptions();
          },
        });
      }

      if (this.filterSlots.includes("estado")) {
        tasks.push({
          run: () => getLiquidacionEstados(),
          onSuccess: (options) => {
            this.estadoOptions = filterLiquidacionEstadoSelectOptions(options);
          },
          onError: () => {
            this.estadoOptions = getDefaultEstadoFilterOptions();
          },
        });
      }

      if (this.filterSlots.includes("proveedor")) {
        tasks.push({
          run: () => getProviders(),
          onSuccess: (options) => {
            this.proveedorOptions = options;
          },
          onError: () => {
            this.proveedorOptions = getDefaultProveedorFilterOptions();
          },
        });
      }

      const results = await Promise.allSettled(tasks.map((task) => task.run()));

      results.forEach((result, index) => {
        const task = tasks[index];

        if (result.status === "fulfilled") {
          task.onSuccess(result.value);
          return;
        }

        console.error(result.reason);
        task.onError();
      });

      this.loadingCatalogs = false;
    },

    updateFilter(key, value) {
      this.$emit("update:filters", normalizeFiltroLiquidacionFilters({
        ...this.filters,
        [key]: value,
      }));
    },
  },
};
</script>
